import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ChevronDown,
  Dices,
  Droplet,
  Palette,
  Moon,
  Sun,
  Type,
  Sparkles,
  RotateCcw,
  X,
  Square,
  Layers,
  Zap,
  SlidersHorizontal,
} from "lucide-react";
import { cva } from "class-variance-authority";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { HTMLPreview } from "./HTMLPreview";
import {
  useStylesBuilderStore,
  STYLE_OPTIONS,
  THEME_OPTIONS,
  ACCENT_COLOR_OPTIONS,
  TYPOGRAPHY_OPTIONS,
  SPACING_OPTIONS,
  ANIMATION_OPTIONS,
  CORNER_STYLE_OPTIONS,
  SHADOW_OPTIONS,
  PRESETS,
  type StyleType,
  type ThemeType,
  type AccentColor,
  type TypographyType,
  type SpacingType,
  type AnimationType,
  type CornerStyleType,
  type ShadowType,
} from "@/store/zustand/stylesBuilderStore";

const selectionOptionVariants = cva(
  "px-2.5 py-1 text-xs rounded-md border transition-all duration-150 cursor-pointer select-none",
  {
    variants: {
      state: {
        default:
          "border-secondary-n-800 bg-background-secondary text-text-secondary hover:border-secondary-n-600 hover:bg-background-tertiary hover:text-text-primary hover:scale-[1.02] active:scale-[0.98]",
        selected:
          "border-primary-n-600 bg-primary-n-900/20 text-text-primary shadow-xs shadow-primary-n-900/20",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

interface SelectionOptionProps<T extends string> {
  value: T;
  label: string;
  isSelected: boolean;
  onSelect: (value: T | null) => void;
}

const SelectionOption = React.memo(function SelectionOption<T extends string>({
  value,
  label,
  isSelected,
  onSelect,
}: SelectionOptionProps<T>) {
  return (
    <button
      type="button"
      onClick={() => onSelect(isSelected ? null : value)}
      aria-pressed={isSelected}
      className={selectionOptionVariants({
        state: isSelected ? "selected" : "default",
      })}
    >
      {label}
    </button>
  );
}) as <T extends string>(props: SelectionOptionProps<T>) => JSX.Element;

interface ColorSwatchProps {
  hex: string;
  isSelected: boolean;
  onSelect: () => void;
  tooltip?: string;
}

const ColorSwatch = React.memo(function ColorSwatch({
  hex,
  isSelected,
  onSelect,
  tooltip,
}: ColorSwatchProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={tooltip}
      aria-label={`${tooltip || "Color"} ${hex}${isSelected ? ", selected" : ""}`}
      aria-pressed={isSelected}
      className={cn(
        "w-4 h-4 rounded-full transition-all duration-150 shrink-0",
        "hover:scale-110 active:scale-95",
        isSelected &&
          "ring-2 ring-primary-n-600 ring-offset-1 ring-offset-background-primary",
      )}
      style={{ backgroundColor: hex }}
    />
  );
});

interface AccentColorPickerProps {
  selectedColor: AccentColor | null;
  customHex: string | null;
  onSelectPreset: (color: AccentColor | null) => void;
  onSelectCustom: (hex: string) => void;
}

const AccentColorPicker = React.memo(function AccentColorPicker({
  selectedColor,
  customHex,
  onSelectPreset,
  onSelectCustom,
}: AccentColorPickerProps) {
  const [localCustomHex, setLocalCustomHex] = useState(customHex || "#6366F1");
  const isCustomSelected = customHex !== null && selectedColor === null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {ACCENT_COLOR_OPTIONS.map((option) => (
        <ColorSwatch
          key={option.value}
          hex={option.hex}
          isSelected={selectedColor === option.value && customHex === null}
          onSelect={() =>
            onSelectPreset(selectedColor === option.value ? null : option.value)
          }
          tooltip={option.label}
        />
      ))}

      {/* Custom color picker */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Custom color"
            aria-label={
              isCustomSelected
                ? `Custom color ${customHex}, selected`
                : "Choose custom color"
            }
            aria-pressed={isCustomSelected}
            className={cn(
              "w-4 h-4 rounded-full transition-all duration-150 shrink-0 border border-dashed",
              "hover:scale-110 active:scale-95 flex items-center justify-center",
              isCustomSelected
                ? "ring-2 ring-primary-n-600 ring-offset-1 ring-offset-background-primary border-transparent"
                : "border-secondary-n-600 hover:border-secondary-n-400",
            )}
            style={
              isCustomSelected ? { backgroundColor: customHex } : undefined
            }
          >
            {!isCustomSelected && (
              <span className="text-xs text-text-muted leading-none">+</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-4 bg-background-secondary border-secondary-n-800"
          align="start"
        >
          <div className="flex flex-col gap-3">
            <div className="text-xs font-medium text-text-primary">
              Custom Color
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={localCustomHex}
                onChange={(e) => setLocalCustomHex(e.target.value)}
                aria-label="Color picker"
                className="w-10 h-10 rounded-md cursor-pointer border-0 p-0"
              />
              <input
                type="text"
                value={localCustomHex}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                    setLocalCustomHex(val);
                  }
                }}
                aria-label="Hex color code"
                className="w-24 px-3 py-1.5 text-xs bg-background-tertiary border border-secondary-n-800 rounded-md text-text-primary focus:border-primary-n-600 focus:outline-hidden transition-colors"
                placeholder="#000000"
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              className="w-full h-8 text-xs"
              onClick={() => onSelectCustom(localCustomHex)}
            >
              Apply Color
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

const OptionDivider = () => (
  <div className="h-px bg-secondary-n-800/50 mx-4 my-1" />
);

interface OptionRowProps {
  label: string;
  value: string | React.ReactNode;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  isLast?: boolean;
}

const OptionRow = React.memo(function OptionRow({
  label,
  value,
  icon,
  isExpanded,
  onToggle,
  children,
  isLast,
}: OptionRowProps) {
  return (
    <div className="transition-all duration-200">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={`${label} options, ${typeof value === "string" ? value : "selected"}`}
        className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-secondary-100 transition-colors duration-150 cursor-pointer group"
      >
        <div className="flex items-center gap-3">
          <span className="text-text-muted group-hover:text-text-secondary transition-colors">
            {icon}
          </span>
          <div className="flex flex-col items-start">
            <span className="text-[10px] text-text-muted uppercase tracking-wide">
              {label}
            </span>
            <span className="text-xs text-text-primary font-medium">
              {value}
            </span>
          </div>
        </div>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-text-muted group-hover:text-text-primary transition-all duration-200",
            isExpanded && "rotate-180",
          )}
        />
      </button>
      {isExpanded && (
        <div
          className={cn(
            "pt-1 pb-3 px-6 flex flex-wrap gap-2",
            !isLast && "border-b border-secondary-n-800",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
});

interface FontOptionRowProps {
  selectedFont: TypographyType | null;
  onSelect: (font: TypographyType | null) => void;
}

const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Bricolage+Grotesque:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&family=Crimson+Pro:wght@400;500;600;700&family=Newsreader:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&family=Fira+Code:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap";

const FontOptionRow = React.memo(function FontOptionRow({
  selectedFont,
  onSelect,
}: FontOptionRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = useMemo(
    () => TYPOGRAPHY_OPTIONS.find((o) => o.value === selectedFont),
    [selectedFont],
  );

  // Load Google Fonts for the preview - only runs once
  useEffect(() => {
    const linkId = "styles-builder-fonts";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = GOOGLE_FONTS_URL;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Font options, ${selectedOption?.label || "none selected"}`}
          className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-secondary-100 transition-colors duration-150 cursor-pointer group"
        >
          <div className="flex items-center gap-3">
            <span className="text-text-muted group-hover:text-text-secondary transition-colors">
              <Type className="w-4 h-4" />
            </span>
            <div className="flex flex-col items-start">
              <span className="text-[10px] text-text-muted uppercase tracking-wide">
                Font
              </span>
              <span
                className="text-xs text-text-primary font-medium"
                style={{ fontFamily: selectedOption?.fontFamily }}
              >
                {selectedOption?.label || "Select"}
              </span>
            </div>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-text-muted group-hover:text-text-primary transition-all duration-200",
              isOpen && "rotate-180",
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[268px] bg-background-secondary border-secondary-n-800"
        align="end"
        sideOffset={4}
      >
        {TYPOGRAPHY_OPTIONS.map((font) => (
          <DropdownMenuItem
            key={font.value}
            className={cn(
              "text-xs cursor-pointer py-2 rounded-none data-highlighted:bg-secondary-n-800",
              selectedFont === font.value &&
                "bg-secondary-n-900 data-highlighted:bg-secondary-n-800",
            )}
            onSelect={() =>
              onSelect(selectedFont === font.value ? null : font.value)
            }
          >
            <span style={{ fontFamily: font.fontFamily }}>{font.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

interface StylesBuilderModalProps {
  onApply: (customAccentHex: string | null) => void;
}

export function StylesBuilderModal({ onApply }: StylesBuilderModalProps) {
  const {
    isOpen,
    closeModal,
    selections,
    setSelection,
    randomizeAll,
    reset,
    hasSelections,
    applyPreset,
    activePresetId,
    setPreviewPreset,
    getEffectiveSelections,
  } = useStylesBuilderStore();

  const effectiveSelections = getEffectiveSelections();

  // Keyboard shortcuts for undo/redo (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key.toLowerCase() !== "z") return;

      e.preventDefault();
      const { undo, redo } = useStylesBuilderStore.temporal.getState();

      if (e.shiftKey) {
        redo();
      } else {
        undo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);
  const [customAccentHex, setCustomAccentHex] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    style: false,
    theme: false,
    accentColor: false,
    spacing: false,
    animation: false,
    radius: false,
    shadow: false,
  });

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(customAccentHex);
    closeModal();
  }, [onApply, customAccentHex, closeModal]);

  const handleSelectPresetColor = useCallback(
    (color: AccentColor | null) => {
      setCustomAccentHex(null);
      setSelection("accentColor", color);
    },
    [setSelection],
  );

  const handleSelectCustomColor = useCallback(
    (hex: string) => {
      setCustomAccentHex(hex);
      setSelection("accentColor", null);
    },
    [setSelection],
  );

  const toggleStyle = useCallback(
    () => toggleSection("style"),
    [toggleSection],
  );
  const toggleTheme = useCallback(
    () => toggleSection("theme"),
    [toggleSection],
  );
  const toggleAccentColor = useCallback(
    () => toggleSection("accentColor"),
    [toggleSection],
  );
  const toggleSpacing = useCallback(
    () => toggleSection("spacing"),
    [toggleSection],
  );
  const toggleAnimation = useCallback(
    () => toggleSection("animation"),
    [toggleSection],
  );
  const toggleRadius = useCallback(
    () => toggleSection("radius"),
    [toggleSection],
  );
  const toggleShadow = useCallback(
    () => toggleSection("shadow"),
    [toggleSection],
  );

  const handleSetStyle = useCallback(
    (v: StyleType | null) => setSelection("style", v),
    [setSelection],
  );
  const handleSetTheme = useCallback(
    (v: ThemeType | null) => setSelection("theme", v),
    [setSelection],
  );
  const handleSetTypography = useCallback(
    (v: TypographyType | null) => setSelection("typography", v),
    [setSelection],
  );
  const handleSetSpacing = useCallback(
    (v: SpacingType | null) => setSelection("spacing", v),
    [setSelection],
  );
  const handleSetAnimation = useCallback(
    (v: AnimationType | null) => setSelection("animation", v),
    [setSelection],
  );
  const handleSetCornerStyle = useCallback(
    (v: CornerStyleType | null) => setSelection("cornerStyle", v),
    [setSelection],
  );
  const handleSetShadow = useCallback(
    (v: ShadowType | null) => setSelection("shadow", v),
    [setSelection],
  );

  const styleLabel = useMemo(
    () =>
      STYLE_OPTIONS.find((o) => o.value === selections.style)?.label ||
      "Select",
    [selections.style],
  );
  const themeLabel = useMemo(
    () =>
      THEME_OPTIONS.find((o) => o.value === selections.theme)?.label ||
      "Select",
    [selections.theme],
  );
  const spacingLabel = useMemo(
    () =>
      SPACING_OPTIONS.find((o) => o.value === selections.spacing)?.label ||
      "Select",
    [selections.spacing],
  );
  const animationLabel = useMemo(
    () =>
      ANIMATION_OPTIONS.find((o) => o.value === selections.animation)?.label ||
      "Select",
    [selections.animation],
  );
  const cornerStyleLabel = useMemo(
    () =>
      CORNER_STYLE_OPTIONS.find((o) => o.value === selections.cornerStyle)
        ?.label || "Select",
    [selections.cornerStyle],
  );
  const shadowLabel = useMemo(
    () =>
      SHADOW_OPTIONS.find((o) => o.value === selections.shadow)?.label ||
      "Select",
    [selections.shadow],
  );
  const accentColorHex = useMemo(
    () =>
      ACCENT_COLOR_OPTIONS.find((c) => c.value === selections.accentColor)?.hex,
    [selections.accentColor],
  );
  const activePresetName = useMemo(
    () =>
      activePresetId
        ? PRESETS.find((p) => p.id === activePresetId)?.name
        : "Custom",
    [activePresetId],
  );

  const accentColorValue = useMemo(() => {
    if (customAccentHex || selections.accentColor) {
      return (
        <span
          className="w-4 h-4 rounded-full inline-block"
          style={{ backgroundColor: customAccentHex || accentColorHex }}
        />
      );
    }
    return "Select";
  }, [customAccentHex, selections.accentColor, accentColorHex]);

  const handleShuffle = useCallback(() => {
    randomizeAll();
    setCustomAccentHex(null);
  }, [randomizeAll]);

  const handleReset = useCallback(() => {
    reset();
    setCustomAccentHex(null);
  }, [reset]);

  const effectiveAccentHex =
    customAccentHex ||
    (effectiveSelections.accentColor
      ? ACCENT_COLOR_OPTIONS.find(
          (c) => c.value === effectiveSelections.accentColor,
        )?.hex
      : null);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent
        hideClose
        className="max-w-[1400px] w-[95vw] max-h-[90vh] h-[850px] overflow-hidden p-0 bg-background-primary border border-secondary-n-900 gap-0 flex flex-row"
      >
        {/* Left Panel - Preview (dominant) */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#1a1a1e]">
          {/* Preview Header */}
          <div className="px-6 py-4 border-b border-secondary-n-800 flex items-center justify-between shrink-0">
            <div>
              <DialogTitle className="text-lg font-semibold text-text-primary">
                Styles Builder
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="rounded-md p-1.5 opacity-70 hover:opacity-100 hover:bg-secondary-100 transition-all"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          {/* Preview Label */}
          <div className="px-6 pt-4 pb-2 shrink-0">
            <span className="text-[11px] font-medium text-text-muted uppercase tracking-wider">
              Preview
            </span>
          </div>

          {/* Preview Content */}
          <div className="flex-1 px-6 pb-6 min-h-0">
            <div className="w-full h-full rounded-lg overflow-hidden border border-secondary-n-800">
              <HTMLPreview
                pageType={effectiveSelections.pageType}
                style={effectiveSelections.style}
                theme={effectiveSelections.theme}
                accentColor={effectiveSelections.accentColor}
                typography={effectiveSelections.typography}
                spacing={effectiveSelections.spacing}
                animation={effectiveSelections.animation}
                cornerStyle={effectiveSelections.cornerStyle}
                shadow={effectiveSelections.shadow}
                customAccentHex={effectiveAccentHex || undefined}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>

        {/* Right Panel - Options Sidebar */}
        <div className="w-[300px] flex flex-col bg-background-primary border-l border-secondary-n-800 min-h-0">
          {/* Preset Selector */}
          <div className="px-4 py-4 border-b border-secondary-n-800 shrink-0">
            <span className="text-xs font-medium text-text-muted uppercase tracking-wider block mb-2">
              Preset
            </span>
            <DropdownMenu
              onOpenChange={(open) => {
                if (!open) setPreviewPreset(null);
              }}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-full h-9 px-3 flex items-center justify-between bg-background-secondary border border-secondary-n-800 rounded-lg text-xs text-text-primary hover:bg-background-tertiary transition-colors"
                >
                  <span className="font-medium">{activePresetName}</span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[288px] bg-background-secondary border-secondary-n-800">
                {PRESETS.map((preset) => (
                  <DropdownMenuItem
                    key={preset.id}
                    className={cn(
                      "text-xs cursor-pointer py-2 data-highlighted:bg-secondary-n-800",
                      activePresetId === preset.id &&
                        "bg-secondary-n-900 data-highlighted:bg-secondary-n-800",
                    )}
                    onMouseEnter={() => setPreviewPreset(preset.id)}
                    onMouseLeave={() => setPreviewPreset(null)}
                    onSelect={() => {
                      applyPreset(preset.id);
                      setCustomAccentHex(null);
                    }}
                  >
                    {preset.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Scrollable Options */}
          <div className="flex-1 overflow-y-auto min-h-0 py-2">
            {/* Visual Style */}
            <OptionRow
              label="Style"
              value={styleLabel}
              icon={<Palette className="w-4 h-4" />}
              isExpanded={expandedSections.style}
              onToggle={toggleStyle}
            >
              {STYLE_OPTIONS.map((option) => (
                <SelectionOption
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selections.style === option.value}
                  onSelect={handleSetStyle}
                />
              ))}
            </OptionRow>

            <OptionDivider />

            {/* Theme */}
            <OptionRow
              label="Theme"
              value={themeLabel}
              icon={
                selections.theme === "light" ? (
                  <Sun className="w-4 h-4" />
                ) : (
                  <Moon className="w-4 h-4" />
                )
              }
              isExpanded={expandedSections.theme}
              onToggle={toggleTheme}
            >
              {THEME_OPTIONS.map((option) => (
                <SelectionOption
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selections.theme === option.value}
                  onSelect={handleSetTheme}
                />
              ))}
            </OptionRow>

            <OptionDivider />

            {/* Accent Color */}
            <OptionRow
              label="Accent Color"
              value={accentColorValue}
              icon={<Droplet className="w-4 h-4" />}
              isExpanded={expandedSections.accentColor}
              onToggle={toggleAccentColor}
            >
              <AccentColorPicker
                selectedColor={selections.accentColor}
                customHex={customAccentHex}
                onSelectPreset={handleSelectPresetColor}
                onSelectCustom={handleSelectCustomColor}
              />
            </OptionRow>

            <OptionDivider />

            {/* Typography */}
            <FontOptionRow
              selectedFont={selections.typography}
              onSelect={handleSetTypography}
            />

            <OptionDivider />

            {/* Spacing */}
            <OptionRow
              label="Spacing"
              value={spacingLabel}
              icon={<SlidersHorizontal className="w-4 h-4" />}
              isExpanded={expandedSections.spacing}
              onToggle={toggleSpacing}
            >
              {SPACING_OPTIONS.map((option) => (
                <SelectionOption
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selections.spacing === option.value}
                  onSelect={handleSetSpacing}
                />
              ))}
            </OptionRow>

            <OptionDivider />

            {/* Animations */}
            <OptionRow
              label="Animations"
              value={animationLabel}
              icon={<Zap className="w-4 h-4" />}
              isExpanded={expandedSections.animation}
              onToggle={toggleAnimation}
            >
              {ANIMATION_OPTIONS.map((option) => (
                <SelectionOption
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selections.animation === option.value}
                  onSelect={handleSetAnimation}
                />
              ))}
            </OptionRow>

            <OptionDivider />

            {/* Corner Style */}
            <OptionRow
              label="Radius"
              value={cornerStyleLabel}
              icon={<Square className="w-4 h-4" />}
              isExpanded={expandedSections.radius}
              onToggle={toggleRadius}
            >
              {CORNER_STYLE_OPTIONS.map((option) => (
                <SelectionOption
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selections.cornerStyle === option.value}
                  onSelect={handleSetCornerStyle}
                />
              ))}
            </OptionRow>

            <OptionDivider />

            {/* Shadow */}
            <OptionRow
              label="Shadow"
              value={shadowLabel}
              icon={<Layers className="w-4 h-4" />}
              isExpanded={expandedSections.shadow}
              onToggle={toggleShadow}
              isLast
            >
              {SHADOW_OPTIONS.map((option) => (
                <SelectionOption
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selections.shadow === option.value}
                  onSelect={handleSetShadow}
                />
              ))}
            </OptionRow>
          </div>

          {/* Footer Actions */}
          <div className="px-4 py-4 border-t border-secondary-n-800 shrink-0 space-y-3">
            {/* Shuffle and Reset row */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleShuffle}
                aria-label="Shuffle all style options randomly"
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg hover:bg-secondary-100 transition-colors duration-150 cursor-pointer group"
              >
                <Dices className="w-4 h-4 text-text-muted group-hover:text-text-secondary" />
                <span className="text-xs text-text-primary font-medium">
                  Shuffle
                </span>
              </button>

              <div className="w-px h-6 bg-secondary-n-800" aria-hidden="true" />

              <button
                type="button"
                onClick={handleReset}
                aria-label="Reset all style options to default"
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg hover:bg-secondary-100 transition-colors duration-150 cursor-pointer group"
              >
                <RotateCcw className="w-4 h-4 text-text-muted group-hover:text-text-secondary" />
                <span className="text-xs text-text-primary font-medium">
                  Reset
                </span>
              </button>
            </div>

            {/* Apply Button */}
            <Button
              variant="primary"
              size="default"
              className="w-full h-9"
              onClick={handleApply}
              disabled={!hasSelections() && !customAccentHex}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Apply Styles
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default StylesBuilderModal;
